import type { PaymentAmountSelection } from "../amountSelection";
import type { PendingPayment } from "../checkoutTypes";
import type { QualpayPaymentSummary, QualpayTokenizedCard } from "../payment";
import { shouldApplySurcharge } from "../surcharge";

export const qualpayPaymentFormId = "qualpay-embedded-form";

export type QpEmbeddedForm = {
  loadFrame: (merchantId: number, settings: QualpayLoadFrameSettings) => void;
  unloadFrame: () => void;
};

export type QualpaySurchargeResult = {
  isDebitPrepaid: boolean;
  isPurchasingCard: boolean;
  isSurchargeAllowed: boolean;
};

export type QualpayLoadFrameSettings = {
  formId: string;
  mode: "test" | "prod";
  transientKey: string;
  tokenize: boolean;
  onSuccess: (response: QualpayCardSuccessResponse) => void;
  onError: (response: { detail?: string[] }) => void;
  preSubmit: () => void;
  achConfig: { enabled: boolean };
  cardConfig: {
    enabled: boolean;
    checkSurchargeAllowed?: (result: QualpaySurchargeResult) => void;
  };
  formFields: { cvv2: { required: boolean } };
  style: string;
};

export type QualpayCardSuccessResponse = {
  card_id: string;
  card_number: string;
  exp_date?: string;
  card_type: "AM" | "DS" | "MC" | "VS" | "AP" | "PP";
};

export function createQualpayPendingPayment({
  card,
  customerId,
  invoiceId,
  payment,
  paymentAmount,
}: {
  card: QualpayTokenizedCard;
  customerId: string;
  invoiceId?: string;
  payment: QualpayPaymentSummary;
  paymentAmount: PaymentAmountSelection;
}): PendingPayment {
  return {
    request: {
      customerId,
      invoiceId,
      amount: paymentAmount.amount,
      applySurcharge: shouldApplySurcharge(payment.surchargePercentage, card.canSurcharge),
      qualpayCard: {
        cardToken: card.cardToken,
        brand: card.brand,
        last4: card.lastFour,
        expirationMonth: card.expirationMonth,
        expirationYear: card.expirationYear,
        nameOnCard: card.nameOnCard,
        zipCode: card.zipCode,
      },
    },
    card,
  };
}

export function createQualpayTokenizedCard(
  response: QualpayCardSuccessResponse,
  nameOnCard: string,
  zipCode: string,
  surchargeResult: QualpaySurchargeResult | null,
): QualpayTokenizedCard | null {
  if (!response.exp_date) return null;

  return {
    processor: "Qualpay",
    cardToken: response.card_id,
    brand: mapQualpayCardType(response.card_type),
    lastFour: response.card_number.slice(-4),
    expirationMonth: Number(response.exp_date.slice(0, 2)),
    expirationYear:
      Number(response.exp_date.slice(-2)) +
      Number(new Date().getFullYear().toString().slice(0, 2)) * 100,
    nameOnCard,
    zipCode,
    canSurcharge: surchargeResult?.isSurchargeAllowed ?? false,
  };
}

function mapQualpayCardType(cardType: QualpayCardSuccessResponse["card_type"]) {
  switch (cardType) {
    case "AM":
      return "American Express";
    case "DS":
      return "Discover";
    case "MC":
      return "MasterCard";
    case "VS":
      return "Visa";
    case "AP":
      return "ACH Payments";
    case "PP":
      return "PayPal";
  }
}

export const qualpayFrameStyle = [
  "html { background: transparent; }",
  "body { background: transparent; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }",
  "body .content { padding: 0 !important; }",
  ".card-type-icon { display: none !important; }",
].join("");
